// screens/RecordHome.tsx — C1 (2026-09-28): the Record window's PRIMARY
// view is Recent — "what was generated on your behalf, by whom" — grouped
// by day, with a search field above it and Registers/Browse folded below.
// Same idiom as RadarHome.tsx: serif say-line, mono eyebrow, hairline
// cards, no new colours.
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  ApiError,
  getRecordActivity,
  getRecordReadingList,
  getRecordRegisters,
  searchRecord,
  type RecordActivityItemApi,
  type RecordBrowseAxisApi,
  type RecordRegisterApi,
  type RecordSearchHitApi,
} from '../lib/api';
import { groupByDay } from '../lib/recordDay';
import './RecordHome.css';

const SEARCH_DEBOUNCE_MS = 300;

export interface RecordHomeProps {
  tenantId: string;
  onOpenPage: (slug: string) => void;
  onOpenFile: (id: string) => void;
  onOpenBrowse: (axis: string) => void;
  onOpenReading: () => void;
  /** H3a (COCKPIT-HOME-COLUMNS-SPEC.md §H3) — a project screen's own
   * "Record window, scoped to <project>". `searchRecord` has no notion of
   * a project/PARA scope to filter by (there is no such axis in this
   * window today), so this is the honest fallback the brief itself names:
   * "if it has no scoping, say so and show the unscoped window with a
   * query" — pre-fills the search field with the project's own keyword
   * rather than inventing a filter that doesn't exist. Reported as a
   * judgement call; true scoping is a later chunk's work once a project
   * axis exists to scope by. */
  initialQuery?: string;
}

type LoadState = { phase: 'loading' } | { phase: 'error'; message: string } | { phase: 'ready' };

const KIND_LABEL: Record<string, string> = { note: 'page', file: 'file', decision: 'decision', radar: 'radar', commit: 'commit' };

const BROWSE_AXES: ReadonlyArray<{ axis: RecordBrowseAxisApi; label: string }> = [
  { axis: 'kind', label: 'Kind' },
  { axis: 'month', label: 'Month' },
  { axis: 'para', label: 'Project · Area · Resource · Archive' },
  { axis: 'note_type', label: 'Type' },
  { axis: 'media_class', label: 'Media' },
];

export function RecordHome({ tenantId, onOpenPage, onOpenFile, onOpenBrowse, onOpenReading, initialQuery }: RecordHomeProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [items, setItems] = useState<RecordActivityItemApi[]>([]);
  const [actors, setActors] = useState<Array<{ actor: string; count: number }>>([]);
  const [nextUntil, setNextUntil] = useState<string | null>(null);
  const [actorFilter, setActorFilter] = useState<string | null>(null);
  const [readingCount, setReadingCount] = useState(0);
  const [registersOpen, setRegistersOpen] = useState(false);
  const [registers, setRegisters] = useState<RecordRegisterApi[] | null>(null);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [query, setQuery] = useState(initialQuery ?? '');
  const [hits, setHits] = useState<RecordSearchHitApi[] | null>(null);
  const [searching, setSearching] = useState(false);
  const reqRef = useRef(0);

  async function refresh(actor: string | null) {
    const reqId = ++reqRef.current;
    try {
      const [activity, reading] = await Promise.all([
        getRecordActivity(tenantId, { limit: 100, actor: actor ?? undefined }),
        getRecordReadingList(tenantId),
      ]);
      if (reqRef.current !== reqId) return;
      setItems(activity.items);
      setActors(activity.actors);
      setNextUntil(activity.nextUntil);
      setReadingCount(reading.filter((r) => !r.readAt).length);
      setLoad({ phase: 'ready' });
    } catch (err) {
      if (reqRef.current !== reqId) return;
      setLoad({ phase: 'error', message: err instanceof ApiError ? err.message : 'the record is not reachable' });
    }
  }

  useEffect(() => {
    setLoad({ phase: 'loading' });
    void refresh(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  function switchActor(actor: string | null) {
    setActorFilter(actor);
    setLoad({ phase: 'loading' });
    void refresh(actor);
  }

  async function loadEarlier() {
    if (!nextUntil) return;
    try {
      const activity = await getRecordActivity(tenantId, { until: nextUntil, limit: 100, actor: actorFilter ?? undefined });
      setItems((cur) => [...cur, ...activity.items]);
      setNextUntil(activity.nextUntil);
    } catch {
      // Earlier is an addition, not the primary load — a failure here
      // leaves the list exactly as it was rather than replacing it with
      // an error.
    }
  }

  function toggleRegisters() {
    setRegistersOpen((o) => !o);
    if (!registers) void getRecordRegisters(tenantId).then(setRegisters).catch(() => setRegisters([]));
  }

  function toggleBrowse() {
    setBrowseOpen((o) => !o);
  }

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setHits(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      searchRecord(tenantId, q, 20)
        .then((r) => setHits(r))
        .catch(() => setHits([]))
        .finally(() => setSearching(false));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, tenantId]);

  function openItem(item: RecordActivityItemApi) {
    if (item.kind === 'file') onOpenFile(item.id);
    else if (item.slug) onOpenPage(item.slug);
    // A decision/radar row with no page of its own yet simply has no
    // click target — its ref names the intended screen; a build with that
    // screen wires it there.
  }

  function openHit(h: RecordSearchHitApi) {
    if (h.kind === 'file') onOpenFile(h.id);
    else if (h.slug) onOpenPage(h.slug);
  }

  const totalCount = actors.reduce((sum, a) => sum + a.count, 0);
  const sayLine =
    readingCount > 0
      ? `${totalCount} thing${totalCount === 1 ? '' : 's'} in the last 7 days · ${readingCount} flagged for you`
      : `${totalCount} thing${totalCount === 1 ? '' : 's'} in the last 7 days`;

  const groups = groupByDay(items);

  return (
    <div class="record">
      <div class="record__eyebrow">Record</div>

      {load.phase === 'loading' ? (
        <div class="record__skeleton" aria-hidden="true">
          <div class="record__skeleton-row" />
          <div class="record__skeleton-row" />
        </div>
      ) : load.phase === 'error' ? (
        <div class="record__say">
          The record is not reachable.
          <br />
          <span class="record__say-soft">{load.message}</span>
        </div>
      ) : (
        <>
          <div class="record__say">
            {sayLine}
            {readingCount > 0 ? (
              <>
                {' — '}
                <button type="button" class="record__link" onClick={onOpenReading}>
                  the reading list
                </button>
              </>
            ) : null}
          </div>

          <input
            type="search"
            class="record__search"
            placeholder="Find in the record"
            value={query}
            onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
          />

          {hits !== null ? (
            <div class="record__list">
              {searching ? (
                <div class="record__empty">Searching…</div>
              ) : hits.length === 0 ? (
                <div class="record__empty">Nothing found.</div>
              ) : (
                hits.map((h) => (
                  <button type="button" class="record__row" key={`${h.kind}-${h.id}`} onClick={() => openHit(h)}>
                    <span class="record__row-top">
                      <span class="record__chip">{KIND_LABEL[h.kind] ?? h.kind}</span>
                      <span class="record__row-title">{h.title}</span>
                    </span>
                    {h.snippet ? <span class="record__row-sub">{h.snippet}</span> : null}
                  </button>
                ))
              )}
            </div>
          ) : (
            <>
              <div class="record__strip">
                <button
                  type="button"
                  class={`record__strip-btn${actorFilter === null ? ' record__strip-btn--active' : ''}`}
                  onClick={() => switchActor(null)}
                >
                  Everyone
                </button>
                {actors.map((a) => (
                  <button
                    type="button"
                    key={a.actor}
                    class={`record__strip-btn${actorFilter === a.actor ? ' record__strip-btn--active' : ''}`}
                    onClick={() => switchActor(a.actor)}
                  >
                    {a.actor} · {a.count}
                  </button>
                ))}
              </div>

              <div class="record__list">
                {groups.length === 0 ? (
                  <div class="record__empty">Nothing recent.</div>
                ) : (
                  groups.map((g) => (
                    <div key={g.label}>
                      <div class="record__day">{g.label}</div>
                      {g.items.map((item) => (
                        <button type="button" class="record__row" key={`${item.kind}-${item.id}`} onClick={() => openItem(item)}>
                          <span class="record__row-top">
                            <span class="record__chip">{KIND_LABEL[item.kind] ?? item.kind}</span>
                            <span class="record__row-title">{item.title}</span>
                            <span class="record__row-when">
                              {new Date(item.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                            </span>
                          </span>
                          <span class="record__row-sub">
                            by {item.actor} · {item.what}
                          </span>
                          {item.summary ? <span class="record__row-sub">{item.summary}</span> : null}
                        </button>
                      ))}
                    </div>
                  ))
                )}
              </div>

              {nextUntil ? (
                <button type="button" class="record__link record__earlier" onClick={() => void loadEarlier()}>
                  Earlier
                </button>
              ) : null}
            </>
          )}

          <button type="button" class="record__fold-toggle" onClick={toggleRegisters}>
            <span class="record__sec">
              <span>Registers</span>
            </span>
          </button>
          {registersOpen ? (
            !registers ? (
              <div class="record__empty">Loading…</div>
            ) : registers.length === 0 ? (
              <div class="record__empty">No registers.</div>
            ) : (
              registers.map((r) => (
                <button
                  type="button"
                  class="record__row"
                  key={r.slug}
                  disabled={!r.title}
                  onClick={() => onOpenPage(r.slug)}
                >
                  <span class="record__row-top">
                    <span class="record__row-title">{r.title ?? r.slug}</span>
                  </span>
                  <span class="record__row-sub">
                    {r.title
                      ? `rev ${r.revision} · updated ${r.updatedAt ? new Date(r.updatedAt).toLocaleDateString([], { month: 'short', day: 'numeric' }) : ''}`
                      : r.error}
                  </span>
                </button>
              ))
            )
          ) : null}

          <button type="button" class="record__fold-toggle" onClick={toggleBrowse}>
            <span class="record__sec">
              <span>Browse</span>
            </span>
          </button>
          {browseOpen ? (
            <div class="record__strip">
              {BROWSE_AXES.map((a) => (
                <button type="button" class="record__strip-btn" key={a.axis} onClick={() => onOpenBrowse(a.axis)}>
                  {a.label}
                </button>
              ))}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
